import { useRef, useState } from "react";
import {
    CalendarIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClipboardDocumentIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";
import { AppointmentAuditDetails } from "./AppointmentAuditDetails";
import {
    APPOINTMENT_TYPE_LIST,
    addAppointmentToTurns,
    formatAppointmentDate,
    formatAppointmentTime,
    formatLongAppointmentDate,
    getAppointmentAvailabilityMeta,
    getAppointmentCardTone,
    getAppointmentDoctorLabel,
    getAppointmentErrorMessage,
    getAppointmentItemKey,
    getAppointmentPatientLabel,
    getAppointmentStatusMeta,
    isTurneroDisabled,
    toErrorMessages,
    toggleAppointmentScheduleLock,
    updateAppointmentStatus,
} from "./appointmentShared";

const Badge = ({ children, className = "" }) => (
    <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}
    >
        {children}
    </span>
);

const DetailItem = ({ label, value }) => (
    <div className="min-w-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
        </span>
        <div className="mt-1 break-words text-sm text-gray-700 dark:text-gray-200">
            {value || (
                <span className="italic text-gray-400 dark:text-gray-500">
                    Sin datos
                </span>
            )}
        </div>
    </div>
);

const TurneroAction = ({ appt, notify, onSave }) => {
    const dialogRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const errorMessages = toErrorMessages(errorMessage);

    const handleSubmit = async () => {
        setLoading(true);
        setErrorMessage("");

        try {
            await addAppointmentToTurns({ appt, notify, onSave });
            dialogRef.current?.close();
        } catch (error) {
            setErrorMessage(getAppointmentErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:bg-gray-800"
                onClick={() => dialogRef.current?.showModal()}
                disabled={isTurneroDisabled(appt)}
            >
                <span className="flex items-center justify-center gap-2">
                    <ClipboardDocumentIcon className="h-5 w-5" />
                    Agregar al turnero
                </span>
            </button>
            <dialog
                ref={dialogRef}
                closedBy="any"
                className="w-[min(28rem,calc(100vw-2rem))] rounded-2xl bg-white px-6 py-5 text-black shadow-xl backdrop:bg-black/30 dark:bg-gray-900 dark:text-white"
            >
                <div className="space-y-4">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">Confirmación</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            ¿Está seguro de que quiere agregar el paciente al
                            turnero?
                        </p>
                        {errorMessages.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {errorMessages.map((message) => (
                                    <p
                                        key={message}
                                        className="text-sm text-red-600"
                                    >
                                        {message}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <button
                            type="button"
                            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-70"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? "Procesando..." : "Aceptar"}
                        </button>
                        <button
                            type="button"
                            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            onClick={() => dialogRef.current?.close()}
                        >
                            Regresar
                        </button>
                    </div>
                </div>
            </dialog>
        </>
    );
};

const ScheduleLockAction = ({ appt, notify, onSave }) => {
    const [loading, setLoading] = useState(false);

    const handleToggle = async () => {
        try {
            setLoading(true);
            await toggleAppointmentScheduleLock({ appt, notify, onSave });
        } catch (error) {
            notify(
                "Hubo un error al actualizar el horario. Inténtalo nuevamente.",
                "error",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            type="button"
            disabled={!!appt.patient || loading}
            onClick={handleToggle}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:bg-gray-800"
        >
            <span className="flex items-center justify-center gap-2">
                <LockClosedIcon className="h-5 w-5" />
                {loading
                    ? "Procesando..."
                    : appt.id
                      ? "Desbloquear horario"
                      : "Bloquear horario"}
            </span>
        </button>
    );
};

const StatusQuickActions = ({ appt, notify, onSave }) => {
    const [loading, setLoading] = useState(false);

    const handleUpdate = async (data) => {
        if (!appt.id) return;

        try {
            setLoading(true);
            await updateAppointmentStatus({ appt, data, notify, onSave });
        } catch (error) {
            notify("Ocurrió un error. Inténtalo de nuevo.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            <button
                type="button"
                disabled={loading || appt?.status === 4 || appt?.status === 5}
                onClick={() =>
                    handleUpdate({ status: 4, time: null, unscheduled: true })
                }
                className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
                Cancelar
            </button>
            <button
                type="button"
                disabled={loading || appt?.status === 3 || appt?.status === 5}
                onClick={() => handleUpdate({ status: 3 })}
                className="rounded-xl border border-green-300 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
                Confirmar
            </button>
        </div>
    );
};

const AppointmentActions = ({
    appt,
    doctorView,
    onRowClick,
    notify,
    onSave,
}) => (
    <div className="space-y-2 border-t border-gray-200/80 pt-4 dark:border-gray-700">
        <button
            type="button"
            disabled={appt.id && !appt.patient}
            onClick={() => onRowClick?.(appt)}
            className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        >
            <span className="flex items-center justify-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                {appt.patient ? "Editar agendamiento" : "Agendar horario"}
            </span>
        </button>
        {doctorView && (
            <ScheduleLockAction appt={appt} notify={notify} onSave={onSave} />
        )}
        {appt.patient && (
            <>
                <TurneroAction appt={appt} notify={notify} onSave={onSave} />
                {appt.status && (
                    <StatusQuickActions
                        appt={appt}
                        notify={notify}
                        onSave={onSave}
                    />
                )}
            </>
        )}
    </div>
);

export const AppointmentListMobile = ({
    data,
    onRowClick,
    loading,
    notify,
    onSave,
    doctorView = false,
}) => {
    const [expandedRow, setExpandedRow] = useState(null);
    const visibleData = doctorView ? data : data.filter((appt) => appt.patient);

    if (loading) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                </div>
            </div>
        );
    }

    if (visibleData.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                No hay citas disponibles
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {visibleData.map((appt) => {
                const key = getAppointmentItemKey(appt);
                const isExpanded = expandedRow === key;
                const tone = getAppointmentCardTone(appt);
                const statusMeta = getAppointmentStatusMeta(appt.status);
                const availabilityMeta = getAppointmentAvailabilityMeta(appt);
                const doctorName = getAppointmentDoctorLabel(appt);
                const patientLabel = getAppointmentPatientLabel(appt);

                return (
                    <div
                        key={key}
                        className={`overflow-hidden rounded-2xl border shadow-sm transition-colors ${tone.card}`}
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setExpandedRow(isExpanded ? null : key)
                            }
                            className={`w-full px-4 py-4 text-left transition-colors ${tone.hover}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        <span>{formatAppointmentDate(appt.date)}</span>
                                        <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                                        <span>{formatAppointmentTime(appt.time)}</span>
                                    </div>
                                    <h3 className="mt-2 truncate text-base font-semibold text-gray-900 dark:text-white">
                                        {patientLabel}
                                    </h3>
                                    <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-300">
                                        {doctorView
                                            ? doctorName
                                            : `Doctor: ${doctorName}`}
                                    </p>
                                </div>
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/80 text-gray-500 shadow-sm dark:bg-gray-900 dark:text-gray-300">
                                    {isExpanded ? (
                                        <ChevronUpIcon className="h-5 w-5" />
                                    ) : (
                                        <ChevronDownIcon className="h-5 w-5" />
                                    )}
                                </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {doctorView && (
                                    <Badge className={availabilityMeta.style}>
                                        {availabilityMeta.text}
                                    </Badge>
                                )}
                                {statusMeta && appt.patient && (
                                    <Badge className={statusMeta.style}>
                                        {statusMeta.text}
                                    </Badge>
                                )}
                                {appt.appointmentType && appt.patient && (
                                    <Badge className="border border-gray-200 bg-white/80 text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                                        {
                                            APPOINTMENT_TYPE_LIST[
                                                appt.appointmentType - 1
                                            ]
                                        }
                                    </Badge>
                                )}
                                {appt.patient?.hasConsultations && (
                                    <Badge className="bg-sky-100 text-sky-700">
                                        Con consultas previas
                                    </Badge>
                                )}
                            </div>
                        </button>

                        {isExpanded && (
                            <div
                                className={`border-t px-4 py-4 ${tone.section}`}
                            >
                                <div className="space-y-4">
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <DetailItem
                                            label="Fecha"
                                            value={formatLongAppointmentDate(appt.date)}
                                        />
                                        <DetailItem
                                            label="Hora"
                                            value={formatAppointmentTime(appt.time)}
                                        />
                                        <DetailItem
                                            label="Doctor"
                                            value={doctorName}
                                        />
                                        {doctorView && (
                                            <DetailItem
                                                label="Disponibilidad"
                                                value={
                                                    <Badge
                                                        className={
                                                            availabilityMeta.style
                                                        }
                                                    >
                                                        {availabilityMeta.text}
                                                    </Badge>
                                                }
                                            />
                                        )}
                                        {appt.patient && (
                                            <DetailItem
                                                label="Paciente"
                                                value={patientLabel}
                                            />
                                        )}
                                        {appt.patient?.govId && (
                                            <DetailItem
                                                label="Documento"
                                                value={appt.patient.govId}
                                            />
                                        )}
                                        {statusMeta && appt.patient && (
                                            <DetailItem
                                                label="Estado"
                                                value={
                                                    <Badge
                                                        className={
                                                            statusMeta.style
                                                        }
                                                    >
                                                        {statusMeta.text}
                                                    </Badge>
                                                }
                                            />
                                        )}
                                        {appt.appointmentType &&
                                            appt.patient && (
                                                <DetailItem
                                                    label="Tipo"
                                                    value={
                                                        APPOINTMENT_TYPE_LIST[
                                                            appt
                                                                .appointmentType -
                                                                1
                                                        ]
                                                    }
                                                />
                                            )}
                                        {(appt.createdBy || appt.patient) && (
                                            <DetailItem
                                                label="Creado por"
                                                value={
                                                    appt.createdBy || "Paciente"
                                                }
                                            />
                                        )}
                                    </div>

                                    <AppointmentAuditDetails
                                        appt={appt}
                                        variant="mobile"
                                    />

                                    <AppointmentActions
                                        appt={appt}
                                        doctorView={doctorView}
                                        onRowClick={onRowClick}
                                        notify={notify}
                                        onSave={onSave}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
